import API from "utils/api";
import { findCertType, formatCertDate } from "utils/ugcCertTypes";

/**
 * Loads the brand record and its certified products in parallel.
 *
 * Resolves to { status, brand, products }:
 *   "ready"    - the brand exists; products may be empty
 *   "notFound" - the brand endpoint answered 404
 *   "error"    - anything else; says nothing about the brand
 *
 * A failed product list alone does not fail the page: the brand record is
 * still true, so it renders with products = null ("could not load"), which
 * is distinct from [] ("none certified").
 */
export async function loadBrand(authId) {
  const id = encodeURIComponent(authId);
  const [brandResult, productsResult] = await Promise.allSettled([
    API.get("/manuf/public/" + id),
    API.get("/manuf/public/" + id + "/certified-products"),
  ]);

  if (brandResult.status === "rejected") {
    const res = brandResult.reason && brandResult.reason.response;
    return {
      status: res && res.status === 404 ? "notFound" : "error",
      brand: null,
      products: null,
    };
  }
  const brand = brandResult.value.data;
  if (!brand || !brand.authId) {
    return { status: "error", brand: null, products: null };
  }

  const products =
    productsResult.status === "fulfilled" &&
    Array.isArray(productsResult.value.data)
      ? productsResult.value.data
      : null;
  return { status: "ready", brand, products };
}

/** Name order that respects Hungarian accents ("Általános" after "Ablak"). */
export function sortProducts(products, locale) {
  if (!Array.isArray(products)) return products;
  return [...products].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", locale, { sensitivity: "base" })
  );
}

/**
 * The certification line: { label, date } or null when the brand carries no
 * known cert code - which renders nothing, as CertString does.
 *
 * For a live certification the audit date is normally its start ("since"),
 * but some rows hold the expiry instead: a future date under ACTIVE can only
 * be one, and "since 2027" would be false - so it reads "valid until".
 */
export function certLine(brand, types, t, today = new Date()) {
  const certType = findCertType(brand.ultraGreenCert, types);
  if (!certType) return null;

  const active = certType.status === "ACTIVE";
  let label = certType.label;
  if (active && t.scope && t.scope[certType.scope]) {
    label =
      t.scope[certType.scope] +
      (certType.coversManufacturer ? t.andManufacturer : "");
  } else if (!active && t.status && t.status[certType.status]) {
    label = t.status[certType.status];
  }

  const iso = formatCertDate(brand.ultraGreenCertAuditDate);
  let date = null;
  if (iso) {
    const shown = formatDay(iso, t.dateLocale);
    if (!active) date = t.certEnded(shown);
    else if (iso > isoDay(today)) date = t.certUntil(shown);
    else date = t.certSince(shown);
  }
  return { label, date, active };
}

/**
 * A date-only ISO string rendered in the page's locale. Parsed with a local
 * time component so a viewer west of UTC does not see the previous day.
 * The trailing period hu-HU adds ("2027. 06. 22.") is dropped so suffixes
 * attach to the day itself.
 */
export function formatDay(iso, locale) {
  return new Date(iso + "T00:00:00")
    .toLocaleDateString(locale)
    .replace(/\.\s*$/, "");
}

function isoDay(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
